import { defineComponent, onBeforeUnmount, onBeforeMount } from 'vue';
import { AppVisibilityBus } from '@/events/app-visibility-bus';
import { useSubscription } from '@vue/apollo-composable';
import { GqlErrorBus } from '@/events/gql-error-bus';
import { gql } from '@apollo/client/core';
import { useAuthStore } from './../auth';
import { useConnectionStore } from '.';
import { Interval } from '@/types';

const GQL_SUB_HEARTBEAT = gql`
  subscription GQL_SUB_HEARTBEAT {
    heartbeat
  }
`;

const HEARTBEAT_RETRY_INTERVAL = 15000;
const HEARTBEAT_OPERATION_NAME = 'GQL_SUB_HEARTBEAT';

type GqlErrorDetail = {
  networkError?: unknown;
  operation?: {
    operationName?: string;
  };
};

const isHeartbeatNetworkError = (detail: GqlErrorDetail | undefined) =>
  Boolean(detail?.networkError && detail.operation?.operationName === HEARTBEAT_OPERATION_NAME);

export const ConnectionManager = defineComponent({
  name: 'ConnectionManager',
  setup(props: any, context: any) {

    /**
     * Contains ref data that we update via
     * the manager, so the rest of the app
     * can be informed.
     */
    const connection = useConnectionStore();

    const auth = useAuthStore();

    /**
     * Watch for changes to Agent Heartbeat
     */
    const { onResult, restart } = useSubscription(
      GQL_SUB_HEARTBEAT,
      null,
      () => ({
        enabled: !!auth.isReady
      })
    );

    let heartbeatInterval: Interval | null = null;

    function clearHeartbeatInterval() {
      if (!heartbeatInterval) return;
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }

    function retryHeartbeat() {
      clearHeartbeatInterval();
      heartbeatInterval = setInterval(() => {
        if (!connection.heartbeat) {
          console.warn('Retrying heartbeat subscription.')
          restart();
        }
      }, HEARTBEAT_RETRY_INTERVAL)
    }

    const handleVisibilityChange = ({ detail }: CustomEvent<DocumentVisibilityState>) => {
      if (detail === 'visible' && !connection.heartbeat) {
        retryHeartbeat();
      }
    };

    const handleGqlError = ({ detail }: CustomEvent<GqlErrorDetail>) => {
      // If heartbeat is already false, an interval is already retrying.
      if (!isHeartbeatNetworkError(detail) || !connection.heartbeat) return;
      console.warn(`Heartbeat subscription failed, retrying in ${HEARTBEAT_RETRY_INTERVAL/1000} seconds.`)
      connection.heartbeat = false;
      retryHeartbeat();
    };

    onResult((result) => {
      // if we have a heartbeat, but previously we did not, update it
      if (!!result.data?.heartbeat && !connection.heartbeat) {
        connection.heartbeat = !!result.data.heartbeat
        clearHeartbeatInterval();
      }
    });

    onBeforeMount(() => {

      /**
       * When the PWA comes back to the foreground, sometimes
       * subscriptions are lost and we need to reconnect.
       */
      AppVisibilityBus.on('visibility-change', handleVisibilityChange);

      /**
       * When GQL Network Errors occur.
       */
      GqlErrorBus.on('error', handleGqlError);
    });

    onBeforeUnmount(() => {
      AppVisibilityBus.off('visibility-change', handleVisibilityChange);
      GqlErrorBus.off('error', handleGqlError);
      clearHeartbeatInterval();
    });

    return () => context.slots.default({
      status: connection.status
    });
  },
});
